// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL EXPORT UTILITIES - UrbanQ v1.9.6
// Exportar datos financieros a PDF y Excel
// ═══════════════════════════════════════════════════════════════════════════

// Exportar a CSV (compatible con Excel)
export const exportToCSV = (data, filename, headers) => {
    if (!data || data.length === 0) {
        alert('No hay datos para exportar');
        return;
    }

    // Crear encabezados
    const headerRow = headers.join(',');

    // Crear filas de datos
    const rows = data.map(row => {
        return headers.map(header => {
            const value = row[header.toLowerCase().replace(/ /g, '_')] || row[header] || '';
            // Escapar comas y comillas
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        }).join(',');
    });

    // Combinar todo
    const csvContent = [headerRow, ...rows].join('\n');

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Exportar a PDF usando impresion del navegador
export const exportToPDF = (elementId, title, filename) => {
    const element = document.getElementById(elementId);
    if (!element) {
        alert('No se encontro el contenido para exportar');
        return;
    }

    // Crear ventana de impresion
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${title}</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    padding: 20px;
                    color: #333;
                }
                h1 {
                    color: #667eea;
                    border-bottom: 2px solid #667eea;
                    padding-bottom: 10px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 20px 0;
                }
                th, td {
                    border: 1px solid #ddd;
                    padding: 10px;
                    text-align: left;
                }
                th {
                    background: #667eea;
                    color: white;
                }
                tr:nth-child(even) {
                    background: #f9f9f9;
                }
                .positive { color: #48bb78; }
                .negative { color: #e53e3e; }
                .footer {
                    margin-top: 30px;
                    text-align: center;
                    color: #666;
                    font-size: 12px;
                }
                @media print {
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                }
            </style>
        </head>
        <body>
            <h1>${title}</h1>
            <p>Generado el: ${new Date().toLocaleDateString('es-EC', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })}</p>
            ${element.innerHTML}
            <div class="footer">
                <p>Generado por UrbanQ - Sistema de Gestion de Condominios</p>
            </div>
        </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
        printWindow.print();
        printWindow.close();
    }, 500);
};

// Formatear datos de ingresos para exportacion
export const formatIncomeForExport = (incomes) => {
    return incomes.map(income => ({
        Fecha: new Date(income.date).toLocaleDateString('es-EC'),
        Categoria: income.category_name || income.category,
        Descripcion: income.description || '',
        Monto: `$${parseFloat(income.amount).toFixed(2)}`,
        Notas: income.notes || ''
    }));
};

// Formatear datos de gastos para exportacion
export const formatExpenseForExport = (expenses) => {
    return expenses.map(expense => ({
        Fecha: new Date(expense.date).toLocaleDateString('es-EC'),
        Categoria: expense.category_name || expense.category,
        Subcategoria: expense.subcategory_name || expense.subcategory || '',
        Descripcion: expense.description || '',
        Tipo: expense.type || 'Variable',
        Monto: `$${parseFloat(expense.amount).toFixed(2)}`,
        Recurrente: expense.is_recurring ? 'Si' : 'No'
    }));
};

// Formatear resumen mensual para exportacion
export const formatMonthlyForExport = (monthlyData) => {
    return monthlyData.map(month => ({
        Mes: month.name,
        Ingresos: `$${parseFloat(month.income || 0).toFixed(2)}`,
        Gastos: `$${parseFloat(month.expense || 0).toFixed(2)}`,
        Balance: `$${parseFloat(month.savings || 0).toFixed(2)}`,
        'Tasa Ahorro': `${(parseFloat(month.savingsRate || 0) * 100).toFixed(1)}%`
    }));
};

// Componente de botones de exportacion
export const ExportButtons = ({ onExportCSV, onExportPDF, loading = false }) => {
    return (
        <div className="urbanq-finanzas-export-buttons">
            <button
                className="urbanq-finanzas-btn secondary sm"
                onClick={onExportCSV}
                disabled={loading}
                title="Exportar a Excel/CSV"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                Excel
            </button>
            <button
                className="urbanq-finanzas-btn secondary sm"
                onClick={onExportPDF}
                disabled={loading}
                title="Exportar a PDF"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                PDF
            </button>
        </div>
    );
};

export default {
    exportToCSV,
    exportToPDF,
    formatIncomeForExport,
    formatExpenseForExport,
    formatMonthlyForExport,
    ExportButtons
};
